/* eslint-disable react/no-unknown-property */
import {
  Badge,
  Dropdown,
  Icon,
  IconButton,
  Menu,
  MenuItem,
} from '@pypestream/design-system';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TRIGGER_ID = 'ps-help-menu-trigger';
// Same retry budget and timings as the design system's
// `ps-universal-nav-changelog`, whose icon this menu replaces.
const MAX_UNREAD_POLL_ATTEMPTS = 10;
const UNREAD_POLL_INTERVAL_MS = 1000;
const UNREAD_RESYNC_DELAY_MS = 1500;

type Produktly = {
  openChangelog: (opts: { id: number }) => void;
  getChangelogUnreadCount?: (opts: { id: number }) => Promise<number>;
};

const produktly = (): Produktly | undefined =>
  (window as unknown as { Produktly?: Produktly }).Produktly;

/** `ps-universal-nav-help`'s own default URL, read from the element rather
 *  than retyped, so the two can't drift. */
const helpCenterUrl = (): string =>
  (
    document.createElement('ps-universal-nav-help') as unknown as {
      url: string;
    }
  ).url;

/**
 * Project Bobcat (5-stage nav): one help button whose menu holds both "Help
 * center" and "What's new", in place of the separate help and changelog
 * icons. Pro Studio only — the shared design-system components are untouched.
 */
export const HelpMenu: FC<{ changelogId?: number }> = ({ changelogId }) => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const refreshUnread = useCallback(
    async (attempt = 0) => {
      if (changelogId === undefined) return;
      clearTimeout(timer.current);
      const count = await produktly()?.getChangelogUnreadCount?.({
        id: changelogId,
      });
      if (typeof count === 'number') {
        setUnread(count);
      } else if (attempt < MAX_UNREAD_POLL_ATTEMPTS) {
        timer.current = setTimeout(
          () => void refreshUnread(attempt + 1),
          UNREAD_POLL_INTERVAL_MS
        );
      }
    },
    [changelogId]
  );

  useEffect(() => {
    void refreshUnread();
    return () => clearTimeout(timer.current);
  }, [refreshUnread]);

  const openHelpCenter = () => {
    setOpen(false);
    window.open(helpCenterUrl(), '_blank');
  };

  const openWhatsNew = () => {
    setOpen(false);
    const p = produktly();
    if (changelogId === undefined || !p) {
      // eslint-disable-next-line no-console
      console.warn('[help-menu] Produktly is not loaded on this page.');
      return;
    }
    p.openChangelog({ id: changelogId });
    // Opening marks posts read; clear now, then re-sync the real count.
    setUnread(0);
    timer.current = setTimeout(
      () => void refreshUnread(),
      UNREAD_RESYNC_DELAY_MS
    );
  };

  return (
    <>
      <Badge variant="danger" count={unread}>
        <IconButton
          id={TRIGGER_ID}
          name="help"
          test-id="ps-help-menu-trigger"
          aria-label="Help"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        />
      </Badge>
      {/* Same controlled, portalled Dropdown as the nav's "More" menu
          (nav-links-overflow.tsx) — see its comment for why the portal is
          unconditional. */}
      {createPortal(
        <Dropdown
          trigger={TRIGGER_ID}
          active={open}
          placement="bottom-end"
          disableTriggerListeners
          omitPortal
          omit-portal
          keepMounted
          test-id="ps-help-menu"
          onclose={() => setOpen(false)}
        >
          <Menu>
            <MenuItem
              test-id="ps-help-menu-help-center"
              onClick={openHelpCenter}
            >
              <Icon size="small" name="help" slot="prefix" />
              Help center
            </MenuItem>
            {changelogId !== undefined && (
              <MenuItem test-id="ps-help-menu-whats-new" onClick={openWhatsNew}>
                <Icon size="small" name="changelog" slot="prefix" />
                What&apos;s new
              </MenuItem>
            )}
          </Menu>
        </Dropdown>,
        document.body
      )}
    </>
  );
};
